import { Container } from './styled';
import { HEADERS } from './fixtures';
import PropTypes from 'prop-types';
import React from 'react';
import { StatisticsLayout } from 'views/components';
import isEmpty from 'lodash/isEmpty';
import { statisticsActions } from 'core/Statistics';
import { useTranslation } from 'react-i18next';
import {
    BarChart,
    Card,
    InfoCard,
    PageController,
    PieChart,
    Table
} from 'mol-ui';
import {
    Col,
    Row
} from 'reactstrap';
import {
    isLocked,
    objectToChart,
    parseAbsenceReportStatus,
    parseSelect
} from 'shared/utils';
import {
    useDispatch,
    useSelector,
    useWindowSize
} from 'hooks';

const AbsenceReports = ({
    tabController
}) => {

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { xs } = useWindowSize();
    const { user } = useSelector('account');
    const {
        absenceReports,
        absenceReportsDownload,
        absenceReportsDownloading,
        absenceReportsPage,
        absenceReportsPageLoading,
        loading
    } = useSelector('statistics');

    const onFiltersChange = filters => {
        dispatch(statisticsActions.getStatisticsAbsenceReports(filters));
        dispatch(statisticsActions.getStatisticsAbsenceReportsPage(filters));
    };

    const {
        absence_reports = 0,
        certificates,
        days = 0,
        family = {},
        reasons = [],
        status
    } = absenceReports;

    const downloadController = {
        data: absenceReportsDownload,
        downloading: absenceReportsDownloading,
        fileName: t('Informes de ausencia'),
        headers: HEADERS,
        onDownload: filters => dispatch(statisticsActions.getStatisticsAbsenceReportsDownload(filters))
    };

    const parseCertificatesKeys = key => {
        switch (key) {
        case ('with'): return t('Con');
        case ('without'): return t('Sin');
        default: return '-';
        }
    };

    const parseFamiliarKeys = key => {
        switch (key) {
        case ('familiar'): return t('Familiar');
        case ('own'): return t('Propia');
        default: return '-';
        }
    };

    const certificatesConfig = {
        config: [{ dataKey: 'value', fontSize: xs ? '11px' : '12px', label: t('Días por certificado') }],
        data: objectToChart(certificates, parseCertificatesKeys),
        height: 300
    };

    const familyConfig = {
        config: [{ dataKey: 'value', fontSize: xs ? '11px' : '12px', label: t('Días por familia') }],
        data: objectToChart(family, parseFamiliarKeys),
        height: 300
    };

    const reasonConfig = {
        config: [{ dataKey: 'value', fontSize: xs ? '11px' : '12px', label: t('Días por razón') }],
        data: parseSelect(reasons, 'name', 'days').map(r => ({ ...r, key: r?.name })),
        height: 300,
        minWidth: 450,
        vertical: true,
        xAxisVerticalHeight: 100
    };

    const statusConfig = {
        config: [{ dataKey: 'value', fontSize: xs ? '11px' : '12px', label: t('Estado') }],
        data: objectToChart(status, parseAbsenceReportStatus),
        height: 300,
        minWidth: 600
    };

    const { data: absenceReportsPageData } = absenceReportsPage ? absenceReportsPage.getData() : { data: [] };

    return (
        <StatisticsLayout
            downloadController={ !isLocked(user, 'download_reports') ? downloadController : undefined }
            filterPatients={ ['area', 'cost_center', 'hr_head', 'patient', 'position', 'workplace'] }
            loading={ loading }
            tabController={ tabController }
            onFiltersChange={ onFiltersChange }
        >
            <Container>
                <Row>
                    <Col md="6" xs="12">
                        <InfoCard
                            caption={ t('Cantidad de informes') }
                            icon="summarize"
                            title={ (absence_reports ?? 0).toString() }
                        />
                    </Col>
                    <Col md="6" xs="12">
                        <InfoCard
                            caption={ t('Total de días informados') }
                            icon="calendar_month"
                            title={ (days ?? 0).toString() }
                        />
                    </Col>
                    {
                        !!absence_reports &&
                        <>
                            <Col xs="12">
                                <Card overflowX="auto" title={ t('Estados') }>
                                    <PieChart { ...statusConfig } />
                                </Card>
                            </Col>
                            {
                                !isEmpty(certificates) &&
                                <Col md="6" xs="12">
                                    <Card overflowX="auto" title={ t('Días por certificado') }>
                                        <PieChart { ...certificatesConfig } />
                                    </Card>
                                </Col>
                            }
                            {
                                !isEmpty(family) &&
                                <Col md="6" xs="12">
                                    <Card overflowX="auto" title={ t('Días por familia') }>
                                        <PieChart { ...familyConfig } />
                                    </Card>
                                </Col>
                            }
                            {
                                !isEmpty(reasons) &&
                                <Col xs="12">
                                    <Card overflowX="auto" title={ t('Días por razón') }>
                                        <BarChart { ...reasonConfig } />
                                    </Card>
                                </Col>
                            }
                        </>
                    }
                </Row>
                {
                    !!absence_reports &&
                    <>
                        <Table
                            config={ HEADERS }
                            data={ absenceReportsPageData }
                            loading={ absenceReportsPageLoading }
                        />
                        <PageController controller={ absenceReportsPage } height="40px" />
                    </>
                }
            </Container>
        </StatisticsLayout>
    );

};

AbsenceReports.propTypes = {
    tabController: PropTypes.object
};

export default AbsenceReports;
